import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import type { MBCBReport } from '../utils/mbcbReportShared';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseMbcbAutosaveOptions {
  reportId: number | null;
  enabled: boolean;
  buildPayload: () => Record<string, unknown>;
  debounceMs?: number;
}

export function useMbcbAutosave({
  reportId,
  enabled,
  buildPayload,
  debounceMs = 1500,
}: UseMbcbAutosaveOptions) {
  const queryClient = useQueryClient();
  const setSaveStatusRef = useRef<(s: SaveStatus) => void>(() => {});
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const buildPayloadRef = useRef(buildPayload);
  buildPayloadRef.current = buildPayload;

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!reportId) throw new Error('No report');
      const res = await apiClient.put(`/mbcb/reports/${reportId}`, buildPayloadRef.current());
      return res.data.data as MBCBReport;
    },
    onMutate: () => {
      setSaveStatusRef.current('saving');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mbcbReports', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['mbcbReport', reportId] });
      setSaveStatusRef.current('saved');
      setTimeout(() => setSaveStatusRef.current('idle'), 2500);
    },
    onError: () => {
      setSaveStatusRef.current('error');
      setTimeout(() => setSaveStatusRef.current('idle'), 4000);
    },
  });

  const saveNow = useCallback(() => {
    if (!enabled || !reportId) return Promise.resolve();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    return saveMutation.mutateAsync().catch(() => undefined);
  }, [enabled, reportId, saveMutation]);

  const scheduleSave = useCallback(() => {
    if (!enabled || !reportId) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      saveMutation.mutate();
    }, debounceMs);
  }, [enabled, reportId, debounceMs, saveMutation]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const bindSaveStatus = useCallback((setter: (s: SaveStatus) => void) => {
    setSaveStatusRef.current = setter;
  }, []);

  return {
    saveNow,
    scheduleSave,
    bindSaveStatus,
    isSaving: saveMutation.isPending,
  };
}

export type { SaveStatus };
